import { useState, useMemo, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, Loader2, Check, Circle } from 'lucide-react';
import { updatePassword } from '../../core/auth/supabase-auth';
import AuthCard from '../../features/auth/components/AuthCard';

interface PasswordRules {
  minLength: boolean;
  lowercase: boolean;
  uppercase: boolean;
  digit: boolean;
  symbol: boolean;
}

const evaluatePassword = (pwd: string): PasswordRules => ({
  minLength: pwd.length >= 8,
  lowercase: /[a-z]/.test(pwd),
  uppercase: /[A-Z]/.test(pwd),
  digit: /[0-9]/.test(pwd),
  symbol: /[^A-Za-z0-9]/.test(pwd),
});

const allRulesPass = (rules: PasswordRules): boolean =>
  Object.values(rules).every(Boolean);

const RuleRow = ({ ok, label }: { ok: boolean; label: string }) => (
  <li className="flex items-center gap-2 text-[12.5px]">
    {ok ? (
      <Check className="h-3.5 w-3.5 text-brand-teal" strokeWidth={3} />
    ) : (
      <Circle className="h-3.5 w-3.5 text-brand-fog/50" strokeWidth={2} />
    )}
    <span className={ok ? 'text-brand-navy' : 'text-brand-fog'}>{label}</span>
  </li>
);

const ResetPasswordPage = () => {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const rules = useMemo(() => evaluatePassword(password), [password]);
  const passwordValid = allRulesPass(rules);
  const showRules = password.length > 0;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!passwordValid) {
      setError(t('resetPassword.passwordTooShort'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('resetPassword.passwordMismatch'));
      return;
    }

    try {
      setLoading(true);
      await updatePassword(password);
      navigate('/login', {
        state: { resetSuccess: true },
        replace: true,
      });
    } catch (err) {
      setError((err as Error).message || t('resetPassword.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthCard
      title={t('resetPassword.title')}
      titleAccent={t('resetPassword.titleAccent')}
      subtitle={t('resetPassword.subtitle')}
      footer={
        <Link
          to="/login"
          className="font-medium text-brand-navy underline decoration-brand-teal decoration-2 underline-offset-4 hover:text-brand-teal transition-colors"
        >
          {t('forgotPassword.backToLogin')}
        </Link>
      }
    >
      <form className="space-y-5" onSubmit={handleSubmit} noValidate>
        {error && (
          <div
            role="alert"
            aria-live="polite"
            className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
          >
            {error}
          </div>
        )}

        <div>
          <label
            htmlFor="password"
            className="block text-[13px] font-medium text-brand-ink"
          >
            {t('resetPassword.newPasswordLabel')}
          </label>
          <div className="relative mt-1.5">
            <input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              placeholder={t('resetPassword.newPasswordPlaceholder')}
              aria-describedby="reset-password-rules"
              className="block w-full rounded-lg border border-brand-rule/30 bg-card px-3.5 py-2.5 pr-11 text-[15px] text-brand-ink placeholder:text-brand-fog/70 transition focus:border-brand-teal focus:outline-none focus:ring-2 focus:ring-brand-teal/25 disabled:opacity-60"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={
                showPassword
                  ? t('register.togglePasswordHide')
                  : t('register.togglePasswordShow')
              }
              aria-pressed={showPassword}
              className="absolute inset-y-0 right-0 flex items-center px-3 text-brand-fog hover:text-brand-navy focus-visible:outline-none focus-visible:text-brand-teal"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          {showRules && (
            <div
              id="reset-password-rules"
              className="mt-3 rounded-lg border border-brand-rule/15 bg-brand-paper/60 px-3.5 py-3"
            >
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-brand-fog">
                {t('register.passwordChecklistTitle')}
              </p>
              <ul className="space-y-1.5">
                <RuleRow ok={rules.minLength} label={t('register.passwordRules.minLength')} />
                <RuleRow ok={rules.lowercase} label={t('register.passwordRules.lowercase')} />
                <RuleRow ok={rules.uppercase} label={t('register.passwordRules.uppercase')} />
                <RuleRow ok={rules.digit} label={t('register.passwordRules.digit')} />
                <RuleRow ok={rules.symbol} label={t('register.passwordRules.symbol')} />
              </ul>
            </div>
          )}
        </div>

        <div>
          <label
            htmlFor="confirmPassword"
            className="block text-[13px] font-medium text-brand-ink"
          >
            {t('resetPassword.confirmPasswordLabel')}
          </label>
          <input
            id="confirmPassword"
            name="confirmPassword"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            required
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={loading}
            placeholder={t('resetPassword.confirmPasswordPlaceholder')}
            className="mt-1.5 block w-full rounded-lg border border-brand-rule/30 bg-card px-3.5 py-2.5 text-[15px] text-brand-ink placeholder:text-brand-fog/70 transition focus:border-brand-teal focus:outline-none focus:ring-2 focus:ring-brand-teal/25 disabled:opacity-60"
          />
        </div>

        <button
          type="submit"
          disabled={loading || !password || !confirmPassword}
          className="group relative inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-brand-teal px-6 text-[14px] font-semibold tracking-tight text-brand-navy transition-all hover:bg-brand-tealLight hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:bg-brand-teal"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('resetPassword.updatingButton')}
            </>
          ) : (
            <>
              {t('resetPassword.submitButton')}
              <span aria-hidden className="transition-transform group-hover:translate-x-0.5">
                →
              </span>
            </>
          )}
        </button>
      </form>
    </AuthCard>
  );
};

export default ResetPasswordPage;
